import { BACKDROPS, GLYPHS, MAP, PALETTE, readableAccent } from "@eldrun/content";
import { PLAN_SIDE } from "@eldrun/worldgen";
import { button, divider, icon, panel, statRow, tabs } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import { fraction } from "../kit/kit-model.ts";
import { MAP_FILTERS, MAP_LEGEND, mapList, markerDetail, pickMarker, scaleBar, type MapFilter, type MapRow } from "./map-list-model.ts";
import { MapView, paintIsland, type FogOfWar, type MapCircle, type MapMarker } from "./map-model.ts";
import { keyHint } from "./settings.ts";

/** Lado (m) da imagem da ilha: a célula (ix, iz) do plano cobre o mundo de (ix·8 − 1024 − 4) a (+4). */
export const ISLAND_IMAGE_SIZE = PLAN_SIDE * 8;
/** Canto noroeste da imagem no mundo (meia célula antes de −1024). */
export const ISLAND_IMAGE_ORIGIN = -1024 - 4;

/**
 * A imagem da ilha e a névoa de exploração em canvas de PLAN_SIDE², para o mapa e o minimapa (F81)
 * dividirem: a névoa só é repintada quando muda (`FogOfWar.dirty`), por quem desenhar primeiro.
 */
export class IslandLayers {
  readonly base: HTMLCanvasElement;
  readonly fog: HTMLCanvasElement;
  private readonly pixels: Uint8ClampedArray<ArrayBuffer>;
  private baseReady = false;

  constructor(plan: { heights: Float32Array; biome: Uint8Array }) {
    this.base = document.createElement("canvas");
    this.fog = document.createElement("canvas");
    this.base.width = this.base.height = this.fog.width = this.fog.height = PLAN_SIDE;
    // Malha neon (F59): a ilha em cinza.
    this.pixels = paintIsland(plan);
  }

  /** Pinta a ilha na primeira vez e a névoa quando mudou. */
  refresh(fog: FogOfWar): void {
    if (!this.baseReady) {
      this.base.getContext("2d")?.putImageData(new ImageData(this.pixels, PLAN_SIDE, PLAN_SIDE), 0, 0);
      this.baseReady = true;
    }
    if (!fog.dirty) return;
    const ctx = this.fog.getContext("2d");
    if (!ctx) return;
    const img = new ImageData(PLAN_SIDE, PLAN_SIDE);
    for (let i = 0; i < fog.cells.length; i++) {
      if (fog.cells[i]) continue;
      img.data[i * 4 + 3] = 205;
    }
    ctx.putImageData(img, 0, 0);
    fog.dirty = false;
  }
}

/** Até quantos pixels de arrasto ainda conta como toque (escolhe o ponto em vez de mover). */
const TAP_PX = 5;

/**
 * Mapa da ilha (doc 05 e 06; F82 no mockup `eldrun-ui-g.png`): no centro, a imagem gerada pela seed
 * (Canvas2D, filtro ponto) com a névoa, o Cerco e os marcadores; à esquerda, a lista "Pontos" com os
 * filtros pela forma; à direita, o detalhe do ponto escolhido. Bússola, + e − do zoom e a escala no mapa;
 * no rodapé, centralizar em você e a legenda. Roda dá zoom em volta do cursor; arrastar move; tocar num
 * marcador o escolhe. M ou Esc fecha.
 */
export class IslandMap {
  readonly element: HTMLElement;
  readonly view = new MapView();
  private readonly canvas: HTMLCanvasElement;
  private readonly layers: IslandLayers;
  private readonly list: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly rows = new Map<string, { root: HTMLButtonElement; symbol: HTMLElement; label: HTMLElement; kind: HTMLElement; distance: HTMLElement; key: string }>();
  private readonly filterTabs: { element: HTMLElement; select(id: string): void };
  private readonly detail: HTMLElement;
  private readonly scaleEl: HTMLElement;
  private readonly scaleLabel: HTMLElement;
  private readonly zoomThumb: HTMLElement;
  private readonly legend: HTMLElement;
  private readonly legendButton: HTMLButtonElement;
  private readonly centerButton: HTMLButtonElement;
  private drag: { x: number; y: number; moved: number } | undefined;
  private filter: MapFilter = "todos";
  private selected: string | undefined;
  private markers: readonly MapMarker[] = [];
  private self: { x: number; z: number } | undefined;
  private detailKey = "";
  /** Fechou pelo Esc ou pelo botão (a cena devolve o foco ao jogo). */
  onClose: () => void = () => {};

  constructor(parent: HTMLElement, layers: IslandLayers) {
    this.canvas = h("canvas", { class: "mapa-tela", "aria-label": "Mapa da ilha" });
    this.layers = layers;

    // Esquerda: "Pontos", com as abas do filtro (a forma de cada grupo) e a lista.
    this.filterTabs = tabs(
      MAP_FILTERS.map((f) => ({ id: f.id, label: f.symbol || f.label })),
      (id) => this.setFilter(id as MapFilter),
    );
    this.filterTabs.element.classList.add("mapa-filtros");
    this.filterTabs.element.querySelectorAll<HTMLElement>("[role=tab]").forEach((tab, i) => {
      const f = MAP_FILTERS[i]!;
      tab.title = f.label;
      tab.setAttribute("aria-label", f.label);
    });
    this.list = h("div", { class: "mapa-lista", role: "listbox", "aria-label": "Pontos no mapa" });
    this.empty = h("p", { class: "mapa-vazio" }, "Nada neste filtro ainda.");
    this.list.addEventListener("click", (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>("[data-ponto]");
      if (row?.dataset["ponto"]) this.choose(row.dataset["ponto"], true);
    });
    const points = panel({ title: "Pontos", icon: "icone-legenda", className: "k-painel--sub mapa-pontos" }, this.filterTabs.element, this.list, this.empty);

    // Centro: a ilha, a bússola, o zoom e a escala; a legenda por cima quando ligada.
    this.zoomThumb = h("span", { class: "mapa-zoom-marca", "aria-hidden": "true" });
    const zoomBy = (factor: number) => {
      const r = this.canvas.getBoundingClientRect();
      this.view.zoomAt(factor, r.width / 2, r.height / 2, r.width, r.height);
      this.updateZoom();
    };
    const zoomButton = (name: "mapa-mais" | "mapa-menos", label: string, factor: number) => h("button", { type: "button", class: "mapa-zoom-botao", "aria-label": label, title: label, onclick: () => zoomBy(factor) }, icon(name));
    this.scaleLabel = h("span", { class: "mapa-escala-texto" });
    this.scaleEl = h("div", { class: "mapa-escala", "aria-hidden": "true" }, h("span", { class: "mapa-escala-zero" }, "0"), this.scaleLabel, h("span", { class: "mapa-escala-barra" }));
    this.legend = h(
      "div",
      { class: "mapa-legenda", role: "list", "aria-label": "Legenda" },
      ...MAP_LEGEND.map((l) => h("div", { class: "mapa-legenda-linha", role: "listitem" }, h("span", { class: "mapa-legenda-simbolo", "aria-hidden": "true" }, l.symbol), h("span", {}, l.label))),
    );
    this.legend.hidden = true;
    const center = h(
      "div",
      { class: "mapa-centro" },
      this.canvas,
      h("span", { class: "mapa-bussola", role: "img", "aria-label": "Norte para cima" }, icon("mapa-bussola")),
      h("div", { class: "mapa-zoom", role: "group", "aria-label": "Zoom" }, zoomButton("mapa-mais", "Aproximar", 1.25), h("span", { class: "mapa-zoom-trilha", "aria-hidden": "true" }, this.zoomThumb), zoomButton("mapa-menos", "Afastar", 0.8)),
      this.scaleEl,
      this.legend,
    );

    // Direita: o detalhe do ponto escolhido.
    this.detail = h("div", { class: "mapa-detalhe-corpo", "aria-live": "polite" });
    const detail = panel({ title: "Detalhe", icon: "icone-detalhe", className: "k-painel--sub mapa-detalhe" }, this.detail);

    // Rodapé: só o que o jogo faz (sem missões nem navegação).
    this.centerButton = button({ label: "Centralizar em mim", icon: "icone-centralizar", variant: "acao", onClick: () => this.centerOnSelf() });
    this.legendButton = button({ label: "Legenda", icon: "icone-legenda", variant: "acao", onClick: () => this.toggleLegend() });
    this.legendButton.setAttribute("aria-pressed", "false");
    const footer = h("div", { class: "mapa-acoes" }, this.centerButton, this.legendButton, h("span", { class: "grow" }), keyHint("map", "Roda: zoom · arrastar: mover · {} ou Esc: fechar"));

    this.element = panel({ title: "Mapa", icon: "icone-mapa", onClose: () => this.toggle(false), closeLabel: "Fechar o mapa", className: "mapa-painel" }, h("div", { class: "mapa-grade" }, points, center, detail), footer);
    this.element.id = "mapa";
    this.element.hidden = true;

    this.canvas.addEventListener("wheel", (e) => {
      e.preventDefault();
      const r = this.canvas.getBoundingClientRect();
      this.view.zoomAt(e.deltaY < 0 ? 1.25 : 0.8, e.clientX - r.left, e.clientY - r.top, r.width, r.height);
      this.updateZoom();
    });
    this.canvas.addEventListener("pointerdown", (e) => {
      this.drag = { x: e.clientX, y: e.clientY, moved: 0 };
      this.canvas.setPointerCapture?.(e.pointerId);
    });
    this.canvas.addEventListener("pointermove", (e) => {
      if (!this.drag) return;
      const r = this.canvas.getBoundingClientRect();
      const dx = e.clientX - this.drag.x;
      const dy = e.clientY - this.drag.y;
      this.view.pan(dx, dy, r.width, r.height);
      this.drag = { x: e.clientX, y: e.clientY, moved: this.drag.moved + Math.hypot(dx, dy) };
    });
    this.canvas.addEventListener("pointerup", (e) => {
      const tap = this.drag !== undefined && this.drag.moved < TAP_PX;
      this.drag = undefined;
      if (!tap) return;
      const r = this.canvas.getBoundingClientRect();
      const hit = pickMarker(this.markers, e.clientX - r.left, e.clientY - r.top, (x, z) => this.view.toScreen(x, z, r.width, r.height));
      if (hit) this.choose(hit.id, false);
    });
    this.canvas.addEventListener("pointercancel", () => (this.drag = undefined));
    this.element.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.toggle(false);
    });
    this.updateZoom();
    this.renderDetail();
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  /** Abre centrado em `center` (o jogador) ou fecha. */
  toggle(open = !this.open, center?: { x: number; z: number }): void {
    const was = this.open;
    this.element.hidden = !open;
    if (open && !was && center) {
      this.view.cx = center.x;
      this.view.cz = center.z;
    }
    if (open) this.element.tabIndex = -1;
    if (open) this.element.focus?.();
    if (!open && was) this.onClose();
  }

  /** Desenha (só aberto): ilha, névoa, círculos do Cerco e marcadores; atualiza a lista e o detalhe. */
  draw(fog: FogOfWar, markers: readonly MapMarker[], circles: readonly MapCircle[]): void {
    if (!this.open) return;
    this.markers = markers;
    const me = markers.find((m) => m.kind === "eu");
    this.self = me && { x: me.x, z: me.z };
    this.centerButton.disabled = this.self === undefined;
    this.renderList();
    this.renderDetail();
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const hgt = Math.max(1, Math.round(rect.height));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (this.canvas.width !== w * dpr || this.canvas.height !== hgt * dpr) {
      this.canvas.width = w * dpr;
      this.canvas.height = hgt * dpr;
    }
    this.layers.refresh(fog);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Fora do mundo: o mesmo mar fundo coberto pela névoa (ninguém explora além da borda).
    ctx.fillStyle = BACKDROPS.fundo;
    ctx.fillRect(0, 0, w, hgt);
    ctx.imageSmoothingEnabled = false;
    // Célula (ix, iz) do plano cobre o mundo de (ix·8 − 1024 − 4) a (+4): a imagem começa meia célula antes.
    const s = this.view.scale(w, hgt);
    const origin = this.view.toScreen(ISLAND_IMAGE_ORIGIN, ISLAND_IMAGE_ORIGIN, w, hgt);
    const size = ISLAND_IMAGE_SIZE * s;
    ctx.drawImage(this.layers.base, origin.x, origin.y, size, size);
    ctx.drawImage(this.layers.fog, origin.x, origin.y, size, size);
    for (const c of circles) {
      const p = this.view.toScreen(c.x, c.z, w, hgt);
      ctx.beginPath();
      ctx.arc(p.x, p.y, c.r * s, 0, Math.PI * 2);
      ctx.lineWidth = c.kind === "cerco" ? 3 : 2;
      ctx.setLineDash(c.kind === "cerco" ? [] : [8, 6]);
      // O Cerco é um perigo neutro (doc 09 §1.3): branco; o próximo, tracejado (F75).
      ctx.strokeStyle = PALETTE.neutro.hex;
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const m of markers) {
      const p = this.view.toScreen(m.x, m.z, w, hgt);
      if (p.x < -20 || p.y < -20 || p.x > w + 20 || p.y > hgt + 20) continue;
      ctx.save();
      ctx.translate(p.x, p.y);
      // O rumo: −z é o "norte" da tela (para cima); o yaw 0 olha para −z.
      if (m.yaw !== undefined) ctx.rotate(-m.yaw);
      ctx.font = m.kind === "eu" ? "bold 18px system-ui, sans-serif" : "15px system-ui, sans-serif";
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(0,0,0,0.8)";
      if (m.kind === "aliado" && /^\d$/.test(m.symbol)) {
        // Aliado (F75): o hexágono na cor do slot com o número dentro (doc 09 §2.6: identidade sem depender da cor).
        ctx.font = "20px system-ui, sans-serif";
        ctx.strokeText(GLYPHS.hexagono.char, 0, 0);
        ctx.fillStyle = m.color;
        ctx.fillText(GLYPHS.hexagono.char, 0, 0);
        ctx.font = "bold 11px system-ui, sans-serif";
        ctx.fillStyle = BACKDROPS.fundo;
        ctx.fillText(m.symbol, 0, 1);
      } else {
        ctx.strokeText(m.symbol, 0, 0);
        ctx.fillStyle = m.color;
        ctx.fillText(m.symbol, 0, 0);
      }
      ctx.restore();
      if (m.id === this.selected) this.drawReticle(ctx, p.x, p.y);
      if (m.kind === "aliado" || m.kind === "ping" || this.view.zoom >= 2.5) {
        // Pings com o rótulo acima e o resto abaixo: aliado e ping no mesmo lugar não se cobrem.
        const ly = m.kind === "ping" ? p.y - 15 : p.y + 15;
        ctx.font = "11px system-ui, sans-serif";
        ctx.lineWidth = 3;
        ctx.strokeStyle = "rgba(0,0,0,0.8)";
        ctx.strokeText(m.label, p.x, ly);
        ctx.fillStyle = PALETTE.neutro.hex;
        ctx.fillText(m.label, p.x, ly);
      }
    }
    // Escala: o maior valor redondo até 120 px de tela.
    const bar = scaleBar(s);
    this.scaleEl.style.setProperty("--largura", `${bar.px}px`);
    if (this.scaleLabel.textContent !== bar.label) this.scaleLabel.textContent = bar.label;
  }

  /** Cantos em volta do ponto escolhido (o alvo do mockup). */
  private drawReticle(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const r = 14;
    const k = 6;
    ctx.save();
    ctx.strokeStyle = PALETTE.neutro.hex;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      ctx.moveTo(x + sx * r, y + sy * (r - k));
      ctx.lineTo(x + sx * r, y + sy * r);
      ctx.lineTo(x + sx * (r - k), y + sy * r);
    }
    ctx.stroke();
    ctx.restore();
  }

  private setFilter(filter: MapFilter): void {
    this.filter = filter;
    this.renderList();
  }

  /** Escolhe um ponto por fora (a vitrine), como pela lista. */
  select(id: string): void {
    this.choose(id, false);
  }

  /** Escolhe um ponto (pela lista centra o mapa nele; pelo toque no mapa, não). */
  private choose(id: string, recenter: boolean): void {
    this.selected = id;
    const m = this.markers.find((k) => k.id === id);
    if (m && recenter) {
      this.view.cx = m.x;
      this.view.cz = m.z;
    }
    this.renderList();
    this.renderDetail();
  }

  private centerOnSelf(): void {
    if (!this.self) return;
    this.view.cx = this.self.x;
    this.view.cz = this.self.z;
  }

  private toggleLegend(): void {
    this.legend.hidden = !this.legend.hidden;
    this.legendButton.setAttribute("aria-pressed", String(!this.legend.hidden));
    this.legendButton.classList.toggle("ligado", !this.legend.hidden);
  }

  /** O ◆ da trilha do zoom (escala logarítmica de 0,25 a 6×). */
  private updateZoom(): void {
    const p = fraction(Math.log(this.view.zoom), Math.log(MAP.zoomMin), Math.log(MAP.zoomMax));
    this.zoomThumb.style.setProperty("--p", String(p));
  }

  /** Lista mantida por id (nós atualizados no lugar: o botão sob o ponteiro não é recriado). */
  private renderList(): void {
    const { rows, counts } = mapList(this.markers, this.self, this.filter);
    // A aba mostra a forma (como o mockup); quantos há vai no nome e na dica.
    this.filterTabs.element.querySelectorAll<HTMLElement>("[role=tab]").forEach((tab, i) => {
      const f = MAP_FILTERS[i]!;
      const text = `${f.label} (${counts[f.id]})`;
      if (tab.title !== text) {
        tab.title = text;
        tab.setAttribute("aria-label", text);
      }
    });
    const seen = new Set<string>();
    const ordered: HTMLElement[] = [];
    for (const r of rows) {
      seen.add(r.id);
      const row = this.row(r);
      const key = `${r.symbol}|${r.color}|${r.label}|${r.kind}|${r.distance}`;
      if (row.key !== key) {
        row.key = key;
        row.symbol.textContent = r.symbol;
        row.symbol.style.setProperty("--cor", r.color);
        row.symbol.style.setProperty("--cor-claro", readableAccent(r.color));
        row.symbol.classList.toggle("aliado", /^\d$/.test(r.symbol));
        row.label.textContent = r.label;
        row.kind.textContent = r.kind;
        row.distance.textContent = r.distance;
        row.root.setAttribute("aria-label", `${r.label}, ${r.kind}, ${r.distance}`);
      }
      const on = r.id === this.selected;
      if (row.root.classList.contains("ativo") !== on) {
        row.root.classList.toggle("ativo", on);
        row.root.setAttribute("aria-selected", String(on));
      }
      ordered.push(row.root);
    }
    for (const [id, row] of this.rows) {
      if (seen.has(id)) continue;
      row.root.remove();
      this.rows.delete(id);
    }
    if (ordered.some((el, i) => this.list.children[i] !== el)) this.list.append(...ordered);
    this.empty.hidden = rows.length > 0;
  }

  private row(r: MapRow) {
    let row = this.rows.get(r.id);
    if (!row) {
      const symbol = h("span", { class: "mapa-ponto-simbolo", "aria-hidden": "true" });
      const label = h("span", { class: "mapa-ponto-nome" });
      const kind = h("span", { class: "mapa-ponto-tipo" });
      const distance = h("span", { class: "mapa-ponto-distancia" });
      const root = h("button", { type: "button", class: "mapa-ponto", role: "option", "aria-selected": "false", "data-ponto": r.id }, symbol, h("span", { class: "mapa-ponto-texto" }, label, kind), distance);
      row = { root, symbol, label, kind, distance, key: "" };
      this.rows.set(r.id, row);
    }
    return row;
  }

  /** O detalhe redesenha só quando o que mostra muda. */
  private renderDetail(): void {
    const marker = this.selected === undefined ? undefined : this.markers.find((m) => m.id === this.selected);
    const d = markerDetail(marker, this.self);
    const key = JSON.stringify(d ?? null);
    if (key === this.detailKey) return;
    this.detailKey = key;
    if (!d) {
      this.detail.replaceChildren(h("p", { class: "mapa-vazio" }, "Escolha um ponto na lista ou toque num marcador do mapa."));
      return;
    }
    const symbol = h("span", { class: `mapa-detalhe-simbolo${/^\d$/.test(d.symbol) ? " aliado" : ""}`, "aria-hidden": "true" }, d.symbol);
    symbol.style.setProperty("--cor", d.color);
    symbol.style.setProperty("--cor-claro", readableAccent(d.color));
    this.detail.replaceChildren(
      h("div", { class: "mapa-detalhe-topo" }, h("span", { class: "mapa-detalhe-moldura" }, symbol), h("div", {}, h("h3", { class: "mapa-detalhe-nome" }, d.title), h("span", { class: "mapa-detalhe-tipo" }, d.kind))),
      divider(),
      h("p", { class: "mapa-detalhe-texto" }, d.text),
      h("div", { class: "mapa-detalhe-linhas" }, ...d.rows.map(([k, v]) => statRow(undefined, k, v))),
    );
  }

  remove(): void {
    this.element.remove();
  }
}
